import { useMemo } from 'react';
import { capitalize, equalInLC } from '@datassential/platform-ui-lib';
import { ByoWord, useByoDataContext } from './ByoContext';

interface PairedFlavor {
    title: string;
    value: string;
    progress: number;
}

interface PairedFlavorsPanel {
    title: string;
    items: PairedFlavor[];
}

const formatPairedFlavorsData = (word: ByoWord): PairedFlavor[] => {
    if (!word?.pairedFlavors) {
        return [];
    }

    return word.pairedFlavors
        .map((flavor) => {
            const numericValue = Number(flavor.value ?? 0);
            const isValidNumber = !Number.isNaN(numericValue);
            
            return {
                title: capitalize(flavor.name || ''),
                value: isValidNumber
                    ? `${numericValue.toFixed(1)}%`
                    : String(flavor.value),
                progress: isValidNumber ? numericValue : 0,
            };
        })
        .slice(0, 5);
};

export const usePairedFlavors = (activeKeywordName?: string | null): PairedFlavorsPanel[] => {
    const { data: keywords } = useByoDataContext();

    return useMemo(() => {
        if (activeKeywordName) {
            const selectedKeyword = keywords.find((word) =>
                equalInLC(word.keyword.name ?? '', activeKeywordName)
            );

            if (selectedKeyword) {
                return [
                    {
                        title: capitalize(activeKeywordName),
                        items: formatPairedFlavorsData(selectedKeyword),
                    },
                ];
            }
            return [];
        }

        return keywords
            .filter(word => (word.pairedFlavors ?? []).length > 0)
            .map((word) => ({
                title: capitalize(word.keyword.name ?? ''),
                items: formatPairedFlavorsData(word),
            }));
    }, [keywords, activeKeywordName]);
};
